import React, { useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  CreditCard,
  Settings,
  BarChart,
  Wrench,
  ArrowRight,
  Check,
} from 'lucide-react';

const SolutionsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      id: 'pos',
      icon: CreditCard,
      title: 'POS & betaling',
      description:
        'Hurtige betalinger ved barer, boder og merchandise – med flere betalingsformer der passer til live events.',
      color: 'text-accent-blue',
      bg: 'bg-accent-blue/10',
      subFeatures: [
        'POS-betaling',
        'Mobilbetaling',
        'Fast Track-betaling',
        'VIP bordbestilling',
      ],
    },
    {
      id: 'operations',
      icon: Settings,
      title: 'Eventdrift',
      description:
        'Planlæg og afvikl events fra ét system – fra opsætning af barer til styring af frivillige, lager og flere salgssteder.',
      color: 'text-accent-purple',
      bg: 'bg-accent-purple/10',
      subFeatures: [
        'Frivilligstyring',
        'Lagerstyring',
        'Opsætning af events',
        'Multi-bar management',
      ],
    },
    {
      id: 'data',
      icon: BarChart,
      title: 'Data og overblik',
      description:
        'Følg salget live under events og brug indsigt til at optimere drift, sortiment og fremtidige arrangementer.',
      color: 'text-accent-pink',
      bg: 'bg-accent-pink/10',
      subFeatures: [
        'Live-dashboard',
        'Salgsanalyse',
        'Overblik per bar eller produkt',
      ],
    },
    {
      id: 'support',
      icon: Wrench,
      title: 'Support',
      description:
        'Hjælp før, under og efter eventet, så driften kører stabilt – også når presset er størst.',
      color: 'text-accent-blue',
      bg: 'bg-accent-blue/10',
      subFeatures: [
        'Onboarding',
        'Opsætningshjælp',
        'Support under event',
        'Løbende sparring',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-white via-secondary/30 to-accent-blue/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight">
                Platformen bag jeres eventdrift
              </h1>
              <p className="text-xl text-body max-w-3xl mx-auto leading-relaxed">
                MUSEIQ samler de vigtigste værktøjer til events i én platform.
                Det giver arrangører bedre overblik, færre systemer og en mere
                effektiv afvikling.
              </p>
            </div>
          </div>
        </section>

        {/* Feature Sections - alternating layout */}
        {sections.map((section, index) => {
          const Icon = section.icon;

          return (
            <section
              key={section.id}
              className={`py-20 ${index % 2 === 1 ? 'bg-secondary/30' : 'bg-white'}`}
            >
              <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                  {/* Text */}
                  <div>
                    <div
                      className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${section.bg} mb-6`}
                    >
                      <Icon className={`w-7 h-7 ${section.color}`} />
                    </div>

                    <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                      {section.title}
                    </h2>

                    <p className="text-lg text-body mb-8 leading-relaxed">
                      {section.description}
                    </p>

                    {section.subFeatures && (
                      <div className="space-y-4">
                        {section.subFeatures.map((sub, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="w-5 h-5 rounded-full bg-accent-blue/10 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <Check className="w-3 h-3 text-accent-blue" />
                            </div>
                            <span className="text-body font-medium">{sub}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* CTA */}
        <section className="cta-section-surface py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Klar til at se MUSEIQ i aktion?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Book en personlig demo og se hvordan platformen kan gøre jeres
              events mere effektive.
            </p>
            <Button
              size="lg"
              className="cta-button-neutral font-semibold px-8 py-4 rounded-xl"
              onClick={() => {
                const subject = encodeURIComponent("Anmodning om demo - MUSEIQ");
                const body = encodeURIComponent("Hej MuseIQ\n\nJeg vil gerne booke en demo af jeres løsning.\n\nI må meget gerne kontakte mig for at aftale et tidspunkt.\n\nBedste hilsner\n[Navn]\n[Virksomhed]");
                window.location.href = `mailto:Kasper@museiq.io?subject=${subject}&body=${body}`;
              }}
            >
              Book demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SolutionsPage;
